/**
 * LiveSiteEventDetailsCard.tsx
 * ----------------
 * Card displaying essential details about a live site event, such as the title and key timestamps.
 */

import * as React from 'react';

import { IcmLink } from './IcmLink';
import { LiveSiteEventSeverity, LiveSiteEventState } from '../../models/status';
import { StatusIndicator } from './StatusIndicator';
import { getTitleForGeography, isAllGeographiesCollection } from '../../utilities/geographies';
import { getDisplayNameForService } from '../../utilities/services';
import { formatUtcAsLocalInEnglishLocale } from '../../utilities/time';

/**
 * Component properties
 */

interface LiveSiteEventImpact {
    geography: string;
    state: LiveSiteEventState;
}

interface LiveSiteEventDetailsCardProps {
    gitHubId?: string;
    icmId?: number;
    impacts: LiveSiteEventImpact[];
    impactStartTimestamp: string;
    includeTimezone?: boolean;
    lastUpdatedTimestamp?: string;
    resolvedTimestamp?: string;
    service: string;
    severity: LiveSiteEventSeverity;
    title: string;
}

/**
 * Private functions
 */

function geographiesForResolvedEvent(impacts: LiveSiteEventImpact[]) {
    return isAllGeographiesCollection(impacts.map((i) => i.geography))
        ? 'All geographies'
        : impacts
              .map((i) => getTitleForGeography(i.geography))
              .sort()
              .join(', ');
}

function geographiesForUnresolvedEvent(impacts: LiveSiteEventImpact[], severity: LiveSiteEventSeverity) {
    return impacts.sort(geographyComparator).map((impact, i) => {
        const { geography, state } = impact;

        return (
            <div className="live-site-event-details-card__geography" key={i}>
                {getTitleForGeography(geography)} <StatusIndicator severity={severity} state={state} />
            </div>
        );
    });
}

function geographyComparator(a: LiveSiteEventImpact, b: LiveSiteEventImpact) {
    const aGeo = getTitleForGeography(a.geography);
    const bGeo = getTitleForGeography(b.geography);

    if (aGeo === undefined && bGeo === undefined) {
        return 0;
    }

    if (aGeo === undefined) {
        return 1;
    }

    if (bGeo === undefined) {
        return -1;
    }

    if (aGeo < bGeo) {
        return -1;
    }

    if (bGeo > aGeo) {
        return 1;
    }

    return 0;
}

/**
 * Card displaying essential details about a live site event, such as the title and key timestamps.
 * @param props Component properties
 * @returns Rendered content
 */
export function LiveSiteEventDetailsCard(props: LiveSiteEventDetailsCardProps) {
    const {
        icmId,
        impacts,
        impactStartTimestamp,
        includeTimezone,
        lastUpdatedTimestamp,
        resolvedTimestamp,
        service,
        severity,
        title,
    } = props;

    // Log warnings for unexpected properties
    if (impacts.some((i) => i.state !== 'Resolved') && resolvedTimestamp !== undefined) {
        console.warn('Unexpected properties: resolved timestamp was given, but not all impacts are resolved.');
    }

    if (lastUpdatedTimestamp === undefined && resolvedTimestamp === undefined) {
        console.warn(
            'Unexpected properties: neither last update nor resolved timestamp were given. Falling back on impact start timestamp.'
        );
    }

    const shouldIncludeTimezone = includeTimezone !== undefined ? includeTimezone : false;
    // NOTE: "Active" is used here to show original severity when incident is not fully resolved
    const stateForTitle = impacts.every((impact) => impact.state === 'Resolved') ? 'Resolved' : 'Active';

    const statusIconStyle: React.CSSProperties = {
        display: 'inline-block',
    };

    const statusIconSvgStyle = {
        className: 'sm-status-icon',
        height: 16,
        width: 16,
    };

    return (
        <div className="live-site-event-details-card ms-depth-8">
            <div className="live-site-event-details-card__row live-site-event-details-card__row--title">
                <div className="live-site-event-details-card__title">
                    <span className="live-site-event-details-card__severity">
                        <StatusIndicator severity={severity} state={stateForTitle} />
                    </span>

                    <h2 className="live-site-event-details-card__header">{title}</h2>
                </div>

                {icmId !== undefined && icmId > 0 && (
                    <div className="live-site-event-details-card__icm-id">
                        <IcmLink icmId={icmId} />
                    </div>
                )}
            </div>

            <div className="live-site-event-details-card__row live-site-event-details-card__row--last hidden--width-800-down">
                <div className="live-site-event-details-card__table-header">
                    <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--header live-site-event-details-card__table-cell--status">
                        Status
                    </div>

                    <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--header live-site-event-details-card__table-cell--service">
                        Service
                    </div>

                    <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--header live-site-event-details-card__table-cell--impact-start-timestamp">
                        Start Time
                    </div>

                    <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--header live-site-event-details-card__table-cell--last-updated-timestamp">
                        {resolvedTimestamp !== undefined ? 'Resolved Time' : 'Last Update'}
                    </div>

                    <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--header live-site-event-details-card__table-cell--impacted-geographies">
                        Impacted Geographies
                    </div>
                </div>

                <div className="live-site-event-details-card__table-data">
                    <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--status">
                        <StatusIndicator
                            inline
                            severity={severity}
                            state={stateForTitle}
                            style={statusIconStyle}
                            svgStyles={statusIconSvgStyle}
                        />
                    </div>

                    <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--service">
                        {getDisplayNameForService(service)}
                    </div>

                    <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--impact-start-timestamp">
                        {formatUtcAsLocalInEnglishLocale(impactStartTimestamp, shouldIncludeTimezone)}
                    </div>

                    <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--last-updated-timestamp">
                        {resolvedTimestamp !== undefined
                            ? formatUtcAsLocalInEnglishLocale(resolvedTimestamp, shouldIncludeTimezone)
                            : lastUpdatedTimestamp !== undefined
                            ? formatUtcAsLocalInEnglishLocale(lastUpdatedTimestamp, shouldIncludeTimezone)
                            : formatUtcAsLocalInEnglishLocale(impactStartTimestamp, shouldIncludeTimezone)}
                    </div>

                    <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--impacted-geographies">
                        {resolvedTimestamp !== undefined && geographiesForResolvedEvent(impacts)}

                        {resolvedTimestamp === undefined && (
                            <div className="live-site-event-details-card__flex-wrap-content">
                                {geographiesForUnresolvedEvent(impacts, severity)}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="live-site-event-details-card__row live-site-event-details-card__row--last live-site-event-details-card__row--reflowing hidden--width-800-up">
                <div className="live-site-event-details-card__table-column">
                    <div className="live-site-event-details-card__table-header">
                        <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--header">
                            Status
                        </div>
                    </div>

                    <div className="live-site-event-details-card__table-data">
                        <div className="live-site-event-details-card__table-cell">
                            <StatusIndicator
                                inline
                                severity={severity}
                                state={stateForTitle}
                                style={statusIconStyle}
                                svgStyles={statusIconSvgStyle}
                            />
                        </div>
                    </div>
                </div>

                <div className="live-site-event-details-card__table-column">
                    <div className="live-site-event-details-card__table-header">
                        <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--header">
                            Service
                        </div>
                    </div>

                    <div className="live-site-event-details-card__table-data">
                        <div className="live-site-event-details-card__table-cell">
                            {getDisplayNameForService(service)}
                        </div>
                    </div>
                </div>

                <div className="live-site-event-details-card__table-column">
                    <div className="live-site-event-details-card__table-header">
                        <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--header">
                            Start Time
                        </div>
                    </div>

                    <div className="live-site-event-details-card__table-data">
                        <div className="live-site-event-details-card__table-cell">
                            {formatUtcAsLocalInEnglishLocale(impactStartTimestamp, shouldIncludeTimezone)}
                        </div>
                    </div>
                </div>

                <div className="live-site-event-details-card__table-column">
                    <div className="live-site-event-details-card__table-header">
                        <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--header">
                            {resolvedTimestamp !== undefined ? 'Resolved Time' : 'Last Update'}
                        </div>
                    </div>

                    <div className="live-site-event-details-card__table-data">
                        <div className="live-site-event-details-card__table-cell">
                            {resolvedTimestamp !== undefined
                                ? formatUtcAsLocalInEnglishLocale(resolvedTimestamp, shouldIncludeTimezone)
                                : lastUpdatedTimestamp !== undefined
                                ? formatUtcAsLocalInEnglishLocale(lastUpdatedTimestamp, shouldIncludeTimezone)
                                : formatUtcAsLocalInEnglishLocale(impactStartTimestamp, shouldIncludeTimezone)}
                        </div>
                    </div>
                </div>

                <div className="live-site-event-details-card__table-column">
                    <div className="live-site-event-details-card__table-header">
                        <div className="live-site-event-details-card__table-cell live-site-event-details-card__table-cell--header">
                            Impacted Geographies
                        </div>
                    </div>

                    <div className="live-site-event-details-card__table-data">
                        <div className="live-site-event-details-card__table-cell">
                            {resolvedTimestamp !== undefined && geographiesForResolvedEvent(impacts)}

                            {resolvedTimestamp === undefined && (
                                <div className="live-site-event-details-card__flex-wrap-content">
                                    {geographiesForUnresolvedEvent(impacts, severity)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
